import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  TEAM_COUNTRY_OPTIONS,
  type TeamCountryCode,
} from '@/constants/teamCountries';

const triggerClassName =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border border-gray-200 bg-white outline-none transition focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-between gap-2 text-left cursor-pointer';

type CountrySelectProps = {
  value: string;
  onChange: (code: TeamCountryCode) => void;
  disabled?: boolean;
  id?: string;
};

export function CountrySelect({
  value,
  onChange,
  disabled,
  id,
}: CountrySelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const selected =
    TEAM_COUNTRY_OPTIONS.find((opt) => opt.code === value) ?? TEAM_COUNTRY_OPTIONS[0];

  const toggle = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, 220) });
    }
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  return (
    <div className="w-full">
      <button
        ref={buttonRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        className={triggerClassName}
      >
        <span className="text-gray-800 text-xs font-normal truncate">{selected.label}</span>
        <i
          className={`ri-arrow-down-s-line text-gray-400 text-lg flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open
        ? createPortal(
            <>
              <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
              <div
                role="listbox"
                aria-labelledby={id}
                className="fixed z-[71] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1"
                style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
              >
                {TEAM_COUNTRY_OPTIONS.map((opt) => {
                  const active = opt.code === value;
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(opt.code);
                        setOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-xs cursor-pointer flex items-center justify-between gap-2 ${
                        active
                          ? 'bg-green-50 text-green-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span>{opt.label}</span>
                      {active ? <i className="ri-check-line text-green-600" /> : null}
                    </button>
                  );
                })}
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}
